import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MonacoEditor from "react-monaco-editor";

import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import { errorText } from "./studioText";

/* The Files tab: the project's files, read-only. The AI is the only editor;
   this is for looking, and for clients who like to see what was made. */

const LANGUAGES = { html: "html", htm: "html", css: "css", js: "javascript", mjs: "javascript", json: "json", rules: "javascript", md: "markdown" };
const languageOf = (path) => LANGUAGES[path.split(".").pop().toLowerCase()] || "plaintext";

function StudioFiles({ projectId, files, version }) {
  const { t } = useTranslation();
  const { user } = useUserAuth();
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState({ loading: false, text: "", binary: false, error: null });

  useEffect(() => {
    if (!selected && files.length) setSelected(files.includes("index.html") ? "index.html" : files[0]);
  }, [files, selected]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setContent((c) => ({ ...c, loading: true, error: null }));
    studioApi
      .getFile(user, projectId, selected, version)
      .then((file) => !cancelled && setContent({ loading: false, text: file.content ?? "", binary: file.binary, error: null }))
      .catch((err) => !cancelled && setContent({ loading: false, text: "", binary: false, error: errorText(t, err) }));
    return () => {
      cancelled = true;
    };
  }, [selected, version, projectId, user, t]);

  return (
    <div className="studio-files">
      <nav className="studio-files__list" aria-label={t("studio.files.title")}>
        {files.map((path) => (
          <button
            key={path}
            type="button"
            className={`studio-files__item ${path === selected ? "studio-files__item-active" : ""}`}
            onClick={() => setSelected(path)}
          >
            {path}
          </button>
        ))}
      </nav>
      <div className="studio-files__view">
        {content.error ? (
          <div className="studio-scroll"><div className="studio-notice"><p>{content.error}</p></div></div>
        ) : content.binary ? (
          <div className="studio-scroll studio-muted">{t("studio.files.binary")}</div>
        ) : (
          <MonacoEditor
            language={selected ? languageOf(selected) : "plaintext"}
            value={content.loading ? "" : content.text}
            options={{ readOnly: true, minimap: { enabled: false }, wordWrap: "on", scrollBeyondLastLine: false, fontSize: 13 }}
            height="100%"
          />
        )}
      </div>
    </div>
  );
}

export default StudioFiles;
